import type { ApiError } from "@iagenix/shared";

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof value.error === "object"
  );
}

/** Fase 9: erros de plano que abrem o aviso global com o CTA de upgrade/regularizar. */
export const PLAN_ERROR_CODES = new Set(["limite_plano", "recurso_fora_do_plano", "modo_leitura"]);
export const PLAN_ERROR_EVENT = "iagenix:plano";

export interface PlanErrorDetail {
  code: string;
  message: string;
  details?: unknown;
}

function announcePlanError(detail: PlanErrorDetail): void {
  try {
    window.dispatchEvent(new CustomEvent<PlanErrorDetail>(PLAN_ERROR_EVENT, { detail }));
  } catch {
    /* fora do navegador */
  }
}

/** Cliente HTTP da API: mesma origem, cookies de sessão, erros com mensagem em pt-BR. */
export async function api<T>(
  path: string,
  init: { method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"; body?: unknown } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  let body: string | null = null;
  if (init.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(init.body);
  }

  let res: Response;
  try {
    res = await fetch(path, {
      method: init.method ?? "GET",
      headers,
      body,
      credentials: "same-origin",
    });
  } catch {
    throw new ApiRequestError(
      0,
      "sem_conexao",
      "Sem conexão com o servidor. Verifique sua internet.",
    );
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    if (isApiError(data)) {
      if (PLAN_ERROR_CODES.has(data.error.code)) {
        announcePlanError({ code: data.error.code, message: data.error.message, details: data.error.details });
      }
      throw new ApiRequestError(
        res.status,
        data.error.code,
        data.error.message,
        data.error.details,
      );
    }
    throw new ApiRequestError(
      res.status,
      "erro_http",
      `O servidor respondeu com erro ${res.status}.`,
    );
  }
  return data as T;
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiRequestError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return "Algo deu errado. Tente de novo.";
}
